import { Injectable } from '@angular/core';
import type { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import type { ButtonType, GamepadButtonAction } from '../../models';
import {
  ButtonDefWithValue,
  defaultModifierThresholds,
  Gamepad,
  GamepadButtonTriggerType,
  isAnalogButtonType,
  isAxisButtonType,
} from '../../models';
import { GamepadConnectionService, triggerActivityThreshold } from './gamepad-connection.service';
import {
  collectModifierTypes,
  resolveActiveModifiers,
  selectActiveActions,
  selectEligibleActions,
} from './gamepad-modifier-resolver';

/**
 * Zeroed rather than emptied: a variable whose source is missing falls back to its configured default
 * instead of reading zero. On a copy, because the live frame is shared by every other action this tick.
 */
const zeroedSnapshot = (gamepad: Gamepad): Gamepad => {
  const snapshot = new Gamepad();

  snapshot.id = gamepad.id;
  snapshot.name = gamepad.name;
  snapshot.type = gamepad.type;
  snapshot.mapping = gamepad.mapping;
  snapshot.pressedValues = gamepad.pressedValues?.map((reading) => {
    const zeroed = new ButtonDefWithValue(reading.type, reading.text, 0);
    zeroed.index = reading.index;
    zeroed.isSessionEndEvent = true;
    return zeroed;
  });

  return snapshot;
};

export class GamepadActionsConfig {
  list: GamepadButtonAction[];
  /** Every input used as a modifier by any action, so only those are threshold-tracked. */
  modifierTypes: Set<ButtonType>;

  constructor(list: GamepadButtonAction[]) {
    // Only the custom gamepad widget can deliver dashboard actions, so this runtime must never fire them.
    this.list = (list ?? []).filter((action) => !action.isDashboardAction);
    this.modifierTypes = collectModifierTypes(this.list);
  }

  /** With no modifiers anywhere every action ties on specificity, so this is the original fan-out. */
  findTargetButtonActions(buttonType: ButtonType, activeModifiers: ReadonlySet<ButtonType>) {
    return selectEligibleActions(this.list, buttonType, activeModifiers);
  }
}

export class GamepadActionEvent {
  action: GamepadButtonAction;
  button: ButtonDefWithValue;
  gamepadSnapshot: Gamepad;

  constructor(action: GamepadButtonAction, button: ButtonDefWithValue, gamepadSnapshot: Gamepad) {
    this.action = action;
    this.button = button;
    this.gamepadSnapshot = gamepadSnapshot;
  }
}

class FiredButtons {
  [buttonType: string]: {
    createdAt: Date;
    updatedAt: Date;
    /** Consecutive frames this input has been present, so 1 is a rising edge. */
    count: number;
    source: ButtonDefWithValue;
  };
}

interface FiredActionRecord {
  lastFiredAt: Date;
  firedCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class GamepadActionService {
  actionsConfig: GamepadActionsConfig;
  firedButtons: FiredButtons = {} as FiredButtons;
  newActionEvent = new Subject<GamepadActionEvent>();
  gamepadPressedSub: Subscription;
  lastGamePadValues: any = {};
  lastGamepadPressedTimeStamp: number;
  safetyGamepadValuesSent: boolean = true;
  /** Which modifiers are currently held. Read by the indicators to show the active layer. */
  activeModifiers: ReadonlySet<ButtonType> = new Set<ButtonType>();

  private gamepadEnabled: boolean;
  private primingFrame = false;
  private modifierThresholds: { [buttonType: number]: number } = defaultModifierThresholds();
  /** Keyed on action identity, which holds only while every lookup goes through the list given to
   *  `setActionsConfigList`. Rebuilding action objects around it misses silently. */
  private firedActions = new Map<GamepadButtonAction, FiredActionRecord>();
  /**
   * Continuous actions mid-stream, so a layer change zeroes exactly what was running. Deriving this
   * from the config instead would stop actions whose input was never touched.
   */
  private streamingActions = new Set<GamepadButtonAction>();

  constructor(private gamepadConnectionService: GamepadConnectionService) {
    this.gamepadConnectionService.gamepadEnabled$.asObservable().subscribe((x) => {
      if (x) {
        // gamepad enable state changed, reset some properties
        this.lastGamepadPressedTimeStamp = null;
        this.safetyGamepadValuesSent = false;
        // The first frame after arming only seeds state, so an input already held when the operator
        // arms is not read as a fresh press.
        this.primingFrame = true;
      }
      this.gamepadEnabled = x;
      this.activeModifiers = new Set<ButtonType>();
      this.firedActions.clear();

      if (x) {
        this.streamingActions.clear();
      }
      // Deliberately kept on disable; `emitStopsForDeselectedActions` drains it next frame.
    });
  }

  setActionsConfigList(list: GamepadButtonAction[]) {
    const config = new GamepadActionsConfig(list);
    this.actionsConfig = config;
    // The old actions are unreachable now, but the map would keep them alive.
    this.firedActions.clear();
    this.streamingActions.clear();

    if (!this.gamepadPressedSub) {
      this.subscribeGamepadPressEvent();
    }
  }

  /** Held here rather than the connection service: this decides which action matches, not how a value is shaped. */
  setModifierThresholds(thresholds: { [buttonType: number]: number }) {
    this.modifierThresholds = { ...defaultModifierThresholds(), ...(thresholds || {}) };
  }

  /**
   * Set the profile's per-axis deadzone map. The shaping itself happens in the connection service
   * so every consumer sees consistent values; this just forwards the configuration.
   */
  setAxisDeadzones(deadzones: { [buttonType: number]: number }) {
    this.gamepadConnectionService.setAxisDeadzones(deadzones);
  }

  /**
   * Set the profile's per-axis expo map. The shaping itself happens in the connection service so
   * every consumer sees consistent values; this just forwards the configuration.
   */
  setAxisExpos(expos: { [buttonType: number]: number }) {
    this.gamepadConnectionService.setAxisExpos(expos);
  }

  subscribeGamepadPressEvent() {
    this.gamepadPressedSub = this.gamepadConnectionService.gamepadPressed$.subscribe((gamepad) => {
      if (gamepad?.pressedValues) {
        if (!this.gamepadEnabled && this.safetyGamepadValuesSent) {
          // gamepad is disabled, and safety gamepad value is already sent, don't process gamepad event
          return;
        }

        // From the raw frame, before the send filter: a modifier threshold may legitimately sit below
        // the send threshold, and which layer is held is a separate question from what is worth sending.
        this.updateActiveModifiers(gamepad);

        // Stop whatever the outgoing layer was streaming before the incoming one starts, so a layer
        // change cannot leave a command holding its last non-zero value.
        this.emitStopsForDeselectedActions(gamepad);

        gamepad = this.gamepadSafetyGuard(gamepad);

        if (this.primingFrame) {
          this.primingFrame = false;
          this.updateFiredButtonsObject(gamepad.pressedValues);
          return;
        }

        if (gamepad.pressedValues.length > 0) {
          this.onGamepadPressed(gamepad);
        }
      }
    });
  }

  gamepadSafetyGuard(gamepad) {
    // The gamepad is disabled, we will send the 0 values once
    if (!this.gamepadEnabled) {
      if (this.safetyGamepadValuesSent) {
        gamepad.pressedValues = [];
        return gamepad;
      }

      this.safetyGamepadValuesSent = true;

      // the gamepad is never been used, don't need to sent 0
      if (!this.lastGamepadPressedTimeStamp) {
        gamepad.pressedValues = [];
        return gamepad;
      }

      // set all received gamepad button values to 0
      gamepad.pressedValues.forEach((element) => {
        element.value = 0;
      });
    }

    gamepad.pressedValues = gamepad.pressedValues.filter((x) => {
      // axies are keeping sending data even not touched, buttons and triggers only send data when they are pressed.
      if (isAxisButtonType(x.type)) {
        // Stick axes are already deadzoned and rescaled by the connection service, so a non-zero
        // value means the stick is outside its deadzone.
        if (x.value !== 0) {
          return true;
        }

        // Within the deadband. If we were outside it last frame, emit a single 0 so the robot stops.
        if (this.lastGamePadValues[String(x.type)]) {
          x.value = 0;
          x.isSessionEndEvent = true;
          return true;
        }

        return false;
      }

      // Non-axis inputs (triggers/buttons) — stick axes are already deadzoned upstream and handled above.
      const threshold = triggerActivityThreshold;
      // A trigger reaches this filter whenever deflected, so that a modifier can match a part pull.
      // Sending still asks for the browser's own `pressed` — the gate a trigger already had.
      const isDeliberate = !isAnalogButtonType(x.type) || x.pressed;
      if (Math.abs(x.value) >= threshold && isDeliberate) {
        // we only send the value when it is greater than the activity threshold
        return true;
      } else if (Math.abs(this.lastGamePadValues[String(x.type)]) >= threshold) {
        // a button only fire value 1, button will never reach here. only trigger and axis will do
        // trigger has less precision, it may never send a value < threshold nor reach here
        // the value is dropped within the deadband, set it to 0
        x.value = 0;
        x.isSessionEndEvent = true;
        return true;
      }

      return false;
    });

    // save current value into cache
    gamepad.pressedValues.forEach((x) => {
      this.lastGamePadValues[String(x.type)] = x.value;
    });
    return gamepad;
  }

  onGamepadPressed(gamepad: Gamepad) {
    const pressedValues: ButtonDefWithValue[] = gamepad.pressedValues;

    if (pressedValues) {
      // Update the firedButton object
      this.updateFiredButtonsObject(pressedValues);

      if (pressedValues.length > 0) {
        this.lastGamepadPressedTimeStamp = Date.now();

        // For each pressed button, check the action and fire an event.
        pressedValues.forEach((item) => {
          this.onNewButtonPressed(item, this.actionsConfig, gamepad);
        });
      }
    }
  }

  onNewButtonPressed(button: ButtonDefWithValue, config: GamepadActionsConfig, gamepad: Gamepad) {
    if (!config) {
      throw new Error('Should set actions config first, please use `setActionsConfigList` to set.');
    }

    const targetActions = config.findTargetButtonActions(button.type, this.activeModifiers);

    if (targetActions?.length > 0) {
      // Got actions
      // For each action, check should fire an event out or not.
      targetActions.forEach((action) => {
        const shouldFire = this.shouldFireAnEvent(button, action);

        if (shouldFire) {
          this.trackStreamingAction(action, button);

          const event: GamepadActionEvent = new GamepadActionEvent(action, button, gamepad);

          this.newActionEvent.next(event);
        }
      });
    }
  }

  /**
   * Update the firedButtons object to handle pressed button events.
   */
  updateFiredButtonsObject(pressedValues: ButtonDefWithValue[]) {
    const updatedFiredButtons: FiredButtons = {};

    if (pressedValues) {
      pressedValues.forEach((item) => {
        const key = item.typeAsString();

        if (this.firedButtons?.[key]) {
          this.firedButtons[key].updatedAt = new Date();
          this.firedButtons[key].count++;
          updatedFiredButtons[key] = this.firedButtons[key];
        } else {
          // Add a new one
          updatedFiredButtons[key] = {
            createdAt: new Date(),
            updatedAt: new Date(),
            count: 1,
            source: item,
          };
        }
      });
    }

    this.firedButtons = updatedFiredButtons;
  }

  shouldFireAnEvent(button: ButtonDefWithValue, action: GamepadButtonAction): boolean {
    const firedButton = this.firedButtons[button.typeAsString()];
    if (!firedButton) {
      throw new Error('Should get firedButton object, but have not found');
    }

    let shouldFire = false;

    const firedAt = new Date();
    const triggerType = action.triggerType;
    const frequency = action.frequency;
    // Per action, not per input: a modified and an unmodified binding on one button must not consume
    // each other's frequency window.
    const firedAction = this.firedActions.get(action);

    switch (triggerType) {
      case GamepadButtonTriggerType.ONCE:
        // `count === 1` is the rising edge of the input, so adding a modifier to an already-held button
        // waits for a fresh press — the same as a keyboard chord. The record below gates nothing here;
        // it is written so getFiredActionRecord reports the same shape for either trigger type.
        if (firedButton.count === 1 && !button.isSessionEndEvent) {
          this.firedActions.set(action, { lastFiredAt: firedAt, firedCount: 1 });
          shouldFire = true;
        }
        break;
      case GamepadButtonTriggerType.CONTINUOUSLY:
        // If have not fired yet, should fire once.
        if (!firedAction || button.isSessionEndEvent) {
          this.firedActions.set(action, { lastFiredAt: firedAt, firedCount: 1 });

          shouldFire = true;
        } else if (firedAt.getTime() - firedAction.lastFiredAt.getTime() >= frequency) {
          this.firedActions.set(action, { lastFiredAt: firedAt, firedCount: firedAction.firedCount + 1 });

          shouldFire = true;
        }
        break;
    }

    return shouldFire;
  }

  /** Last fire per action, for the indicators and for asserting cadence in tests. */
  getFiredActionRecord(action: GamepadButtonAction): FiredActionRecord | undefined {
    return this.firedActions.get(action);
  }

  /** Carries the previous set in for hysteresis. */
  private updateActiveModifiers(gamepad: Gamepad) {
    const modifierTypes = this.actionsConfig?.modifierTypes;

    if (!this.gamepadEnabled || !modifierTypes?.size) {
      this.activeModifiers = new Set<ButtonType>();
      return;
    }

    this.activeModifiers = resolveActiveModifiers(
      gamepad.pressedValues,
      modifierTypes,
      this.modifierThresholds,
      this.activeModifiers,
    );
  }

  /**
   * Immediate rather than waiting for the action's own cadence, which on a once-a-second binding leaves
   * the robot running for up to a second after the layer moved.
   *
   * Keys off selection, not eligibility: a base action stays eligible forever, so an eligibility test
   * would never stop one that a modified binding has out-specified.
   */
  private emitStopsForDeselectedActions(gamepad: Gamepad) {
    if (this.streamingActions.size === 0) {
      return;
    }

    // Disabling deselects everything. The guard's own zero frame goes out through the modifier match,
    // which drops any action whose modifier is no longer held, so a modified binding that was streaming
    // when the operator disarmed would otherwise never be told to stop.
    const selected = this.gamepadEnabled
      ? selectActiveActions(this.actionsConfig?.list ?? [], this.activeModifiers)
      : new Set<GamepadButtonAction>();
    const stopped = Array.from(this.streamingActions).filter((action) => !selected.has(action));

    if (stopped.length === 0) {
      return;
    }

    const snapshot = zeroedSnapshot(gamepad);

    stopped.forEach((action) => {
      this.streamingActions.delete(action);
      // Cleared so re-entering the layer fires straight away instead of waiting out the remainder of
      // a frequency window that belongs to a binding no longer in effect.
      this.firedActions.delete(action);

      const button = new ButtonDefWithValue(action.source?.type, action.source?.text, 0);
      button.isSessionEndEvent = true;

      this.newActionEvent.next(new GamepadActionEvent(action, button, snapshot));
    });
  }

  /** A session-end frame is itself the stop, so it clears the action rather than recording it. */
  private trackStreamingAction(action: GamepadButtonAction, button: ButtonDefWithValue) {
    if (action.triggerType !== GamepadButtonTriggerType.CONTINUOUSLY) {
      return;
    }

    if (button.isSessionEndEvent) {
      this.streamingActions.delete(action);
      return;
    }

    this.streamingActions.add(action);
  }
}
